import { z } from "zod";
import { defineStepConfig } from "../../registry/helpers";
import { WorkflowStepBatchConfigSchema } from "../../types/batch";
import { OPTIONAL_QUANTITY_INPUT, OPTIONAL_TEXT_INPUT } from "./optionalInputs";
import { QUANTITY_INPUT } from "./quantityInput";

/**
 * Upsert Supplier Product
 *
 * Takes ONE supplier-product's fields as single-value input sockets, shaped
 * after the Gently model (`ap_supplier_products` + its price-break child) — not
 * after any source system. A CDC observer or file parser maps its own columns
 * into these sockets, so the same step serves every source; anything
 * source-specific (field naming, sentinel values, code decoding, vendor-code
 * prefix translation) belongs in that upstream mapping.
 *
 * In particular, the supplier is resolved from `supplierCode` as an
 * `ap_suppliers.origin_id`. Where a source's vendor code is not already that
 * value — GAINS writes `W+50109` where the supplier catalog holds `WG50109` —
 * put a `use_data_mapping` step upstream and feed its output here. Keeping the
 * translation in an org-level mapping table means a new prefix is a data edit,
 * not a deploy.
 *
 * The three price-break tiers are fixed columns of the row rather than a
 * variable-length list, so they arrive as six optional sockets and are written
 * to the child table by the same step. A tier is written only when BOTH its
 * quantity and its cost are present; a half-specified tier is dropped and
 * reported, never persisted as a zero.
 *
 * The tier inputs are a COMPLETE SNAPSHOT, not a partial update. A tier left
 * empty is a statement that the supplier does not offer it, so any previously
 * persisted tier not present in this run is DELETED — including one supplied
 * half-specified, since the source touched it and gave us something unusable,
 * and a stale discount that makes the engine UNDER-cost is worse than losing a
 * tier and falling back to `unit_price`. Upserting alone would
 * leave a withdrawn price break in the table and the replenishment engine would
 * keep costing purchases against it. If a source ever needs partial-update
 * semantics it must not use these sockets.
 *
 * The supplier, product and warehouse are resolved from their codes (warehouse
 * resolution optionally scoped by `originSystem`); rows referencing unknown
 * references are skipped and reported per input, never auto-created — the
 * product catalog, warehouse seed and supplier catalog remain the source of
 * truth. With a `batch` config the engine hands whole chunks to the bulk
 * handler. A leaf step: per-row outputs are execution results, not a downstream
 * data contract.
 */
export const upsertSupplierProductStep = defineStepConfig({
  type: "upsert_supplier_product",
  name: "Upsert Supplier Product",
  description:
    "Upserts one supplier product (supplier code, product SKU, warehouse code, unit price, order quantities, up to three price-break tiers). Resolves the supplier, product and warehouse, then writes the row and its price breaks in source-agnostic batches. Fan a trigger's per-field array outputs into its single-value inputs and add a batch config to process rows in chunks.",
  category: "Transformation",
  libraryModule: "Procurement",

  inputSchema: z.object({
    supplierCode: z
      .string()
      .describe("Supplier origin code (resolved to the supplier)"),
    productSku: z.string().describe("Product SKU (resolved to the product)"),
    warehouseCode: z
      .string()
      .describe("Warehouse origin code (resolved to the warehouse)"),
    unitPrice: QUANTITY_INPUT.describe(
      "Unit price. Required — a supplier product with no price cannot be costed by the engine.",
    ),
    name: OPTIONAL_TEXT_INPUT.describe(
      "Display name. Defaults to the product SKU when absent.",
    ),
    minimumOrderQuantity: OPTIONAL_QUANTITY_INPUT.describe(
      "Minimum order quantity (MOQ). Absent is not the same as 0.",
    ),
    incrementalOrderQuantity: OPTIONAL_QUANTITY_INPUT.describe(
      "Order increment above the minimum. Absent is not the same as 0.",
    ),
    leadTimeDays: OPTIONAL_QUANTITY_INPUT.describe(
      "Lead time in days, as quoted by the source. Sources are often wrong about this — prefer a lead time measured from receipt history where one is available.",
    ),
    originId: OPTIONAL_TEXT_INPUT.describe(
      "Stable identifier for this row in the source system. Recorded for traceability; the row's identity is the resolved (supplier, product, warehouse) tuple.",
    ),
    tier1MinQuantity: OPTIONAL_QUANTITY_INPUT.describe(
      "Price-break tier 1: minimum quantity",
    ),
    tier1UnitCost: OPTIONAL_QUANTITY_INPUT.describe(
      "Price-break tier 1: unit cost at that quantity",
    ),
    tier2MinQuantity: OPTIONAL_QUANTITY_INPUT.describe(
      "Price-break tier 2: minimum quantity",
    ),
    tier2UnitCost: OPTIONAL_QUANTITY_INPUT.describe(
      "Price-break tier 2: unit cost at that quantity",
    ),
    tier3MinQuantity: OPTIONAL_QUANTITY_INPUT.describe(
      "Price-break tier 3: minimum quantity",
    ),
    tier3UnitCost: OPTIONAL_QUANTITY_INPUT.describe(
      "Price-break tier 3: unit cost at that quantity",
    ),
  }),

  outputSchema: z.object({
    written: z.boolean().describe("The supplier product row was upserted"),
    skipped: z
      .boolean()
      .describe("The row was skipped because a reference was unresolved"),
    unresolvedSupplier: z
      .boolean()
      .describe("No supplier matched the supplier code"),
    unresolvedProduct: z
      .boolean()
      .describe(
        "No product matched the SKU (ingest the product catalog first)",
      ),
    unresolvedWarehouse: z
      .boolean()
      .describe("No warehouse matched the warehouse code"),
    priceBreaksWritten: z
      .number()
      .describe("How many of the three tiers were persisted (0-3)"),
    incompletePriceBreakTiers: z
      .array(z.number())
      .describe(
        "Tiers given a quantity without a cost, or a cost without a quantity. Dropped rather than written as a zero.",
      ),
  }),

  configSchema: z.object({
    batch: WorkflowStepBatchConfigSchema.prefault({ size: 5000 }),
    originSystem: z
      .string()
      .optional()
      .describe(
        "Restrict WAREHOUSE resolution to references ingested from this origin system. Does not scope products: ProductV3 is keyed by SKU within the organization and carries no origin system. Leave empty to match any.",
      ),
    /**
     * Separate from `originSystem` because the two catalogs are not stamped
     * with the same value: warehouses arrive from the GAINS-shaped ingest while
     * the supplier catalog is written by the vendor ingest under its own label.
     * A single filter would necessarily miss one of them, and the failure mode
     * is silent — every row skipped, the step still reporting success.
     */
    supplierOriginSystem: z
      .string()
      .optional()
      .describe(
        "Restrict SUPPLIER resolution to references ingested from this origin system. Set separately from the warehouse filter: the two catalogs are commonly stamped with different values. Leave empty to match any, in which case a code matching several sources resolves to nothing and is reported.",
      ),
    /**
     * Stamped onto rows this step writes, so a later run can tell which pipeline
     * owns a row. Deliberately separate from the `originSystem` filter above:
     * one narrows what we READ, this labels what we WRITE, and conflating them
     * is how a transform ends up filtering on a value nothing was ever
     * stamped with.
     */
    writeOriginSystem: z
      .string()
      .optional()
      .describe(
        "Value written to the row's origin_system column. Independent of the resolution filter above.",
      ),
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "30 minutes",
    retry: { maximumAttempts: 2 },
  },
});
